import { Trans } from "react-i18next";
import { useState, useEffect } from "react";
import { useParams, useLocation } from "react-router-dom";
import { Download, Lock, CalendarClock, XCircle } from "lucide-react";
import { AppLayout, PageBackLink } from "../../../components/layout";
import { LoadingState } from "../../../components/ui";
import {
  fetchPollById,
  computeResults,
  computeParticipants,
  effectiveStatus,
  canViewResults,
  canSeeRespondents,
  closePoll,
  extendEndDate,
  downloadCsv,
  getCurrentUser,
} from "../../../services/pollServices";
import { PollResultsView } from "../components/PollResultsView";
import { StatusPill, ResponseTypePill } from "../components/StatusPill";
import { ExtendEndDateModal, ClosePollDialog } from "../components/PollActionDialogs";
import { Toast } from "../components/Toast";
import { useOnline } from "../hooks/useOnline";
import { buildResultsCsv } from "../utils/resultsCsv";
import { ROUTES, formatDateTime, describeAudience } from "../constants/pollOptions";

function Meta({ label, value }) {
  return (
    <div>
      <dt className="text-[11px] font-bold uppercase tracking-wider text-[var(--color-text-secondary)]">{label}</dt>
      <dd className="mt-0.5 text-sm text-foreground">{value || "—"}</dd>
    </div>
  );
}

/** One-line summary of who can see the results, and from when. */
function describeVisibility(poll) {
  if (!poll.shareResults && !poll.resultsAfterVoting) return "Creator & HR only";
  if (poll.shareResults && poll.resultsAfterVoting) return "Shared — after voting & after close";
  if (poll.resultsAfterVoting) return "Shared — after voting";
  return "Shared — after close";
}

/** Why the results are locked, phrased for whoever hit the gate. */
function describeLockReason(poll) {
  if (!poll.shareResults && !poll.resultsAfterVoting) {
    return "This poll's results aren't shared with the audience.";
  }
  if (poll.resultsAfterVoting) return "Results unlock once you have cast your vote.";
  return "Results unlock once the poll closes.";
}

/**
 * Screen 9 — Poll Results.
 * Reachable by HR, the creator, and employees whose poll's Results
 * Visibility permits it. HR additionally gets the two post-publish controls
 * (Close Poll, Extend End Date) and CSV export, which unlocks only once the
 * poll is Closed.
 */
export function PollResultsPage({ audience = "creator" }) {
  const { id } = useParams();
  const location = useLocation();
  const user = getCurrentUser();
  const online = useOnline();
  const fromLog = new URLSearchParams(location.search).get("from") === "log";

  const [poll, setPoll] = useState(null);
  const [results, setResults] = useState(null);
  const [participants, setParticipants] = useState(null);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);
  const [busy, setBusy] = useState(false);
  const [actionError, setActionError] = useState("");
  const [extendOpen, setExtendOpen] = useState(false);
  const [closeOpen, setCloseOpen] = useState(false);
  const [toast, setToast] = useState("");

  useEffect(() => {
    let cancelled = false;

    (async () => {
      try {
        const found = await fetchPollById(id);
        if (cancelled) return;
        setPoll(found);

        // Results are a separate call so the visibility rule is enforced by
        // the server rather than inferred here. A 403 is an expected outcome,
        // not a page failure — the gate below renders the reason.
        if (canViewResults(found)) {
          const agg = await computeResults(id).catch(() => null);
          if (!cancelled) setResults(agg);

          if (canSeeRespondents(found, user)) {
            const people = await computeParticipants(id).catch(() => null);
            if (!cancelled) setParticipants(people);
          }
        }
      } catch (err) {
        if (!cancelled) setLoadError(err.message || "Poll not found.");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id]);

  const backTo = audience === "ess" ? ROUTES.essDashboard : fromLog ? ROUTES.hrLog : ROUTES.creatorList;

  if (loading) return <AppLayout><LoadingState /></AppLayout>;

  if (loadError || !poll) {
    return (
      <AppLayout>
        <PageBackLink to={backTo} />
        <p className="p-6 text-sm text-[var(--color-error)]">{loadError ?? "Poll not found."}</p>
      </AppLayout>
    );
  }

  // Results Visibility gate — HR and the creator always retain access.
  // The same rule is re-enforced on the results endpoint, so this is
  // presentation only.
  if (!canViewResults(poll)) {
    return (
      <AppLayout>
        <PageBackLink to={backTo} />
        <div className="max-w-[640px] rounded-2xl bg-white border border-[var(--color-text-border)]/70 p-8 text-center">
          <Lock className="h-6 w-6 mx-auto mb-3 text-[var(--color-text-secondary)]" />
          <p className="text-sm font-semibold text-foreground">
            <Trans i18nKey="polls.resultsArenTAvailableToYou" />
          </p>
          <p className="mt-1 text-sm text-[var(--color-text-secondary)]">{describeLockReason(poll)}</p>
        </div>
      </AppLayout>
    );
  }

  const status = effectiveStatus(poll);
  const isClosed = status === "Closed";
  // Close / Extend / Export are HR-only, as decided server-side.
  const canManage = Boolean(poll.canManage);

  const runAction = async (fn, message) => {
    setBusy(true);
    setActionError("");
    try {
      const updated = await fn();
      setPoll(updated);
      // Closing a poll changes the participation window, so re-read the
      // aggregate rather than leaving the pre-close numbers on screen.
      setResults(await computeResults(id).catch(() => results));
      setToast(message);
    } catch (err) {
      setActionError(err.message || "Action failed. Please try again.");
    } finally {
      setBusy(false);
      setExtendOpen(false);
      setCloseOpen(false);
    }
  };

  return (
    <AppLayout>
      <PageBackLink to={backTo} />

      <div className="max-w-[820px] bg-white rounded-2xl border border-[var(--color-text-border)]/70 shadow-[0_10px_30px_-18px_rgba(30,51,131,0.35)] overflow-hidden">
        <div className="px-6 sm:px-8 py-6 border-b border-[var(--color-text-border)]/70">
          <div className="flex items-center gap-3 mb-3 flex-wrap">
            <StatusPill status={status} />
            <ResponseTypePill responseType={poll.responseType} />
            {poll.anonymous && (
              <span className="text-[12px] font-semibold text-[var(--color-accent)]">
                <Trans i18nKey="polls.anonymous" />
              </span>
            )}
          </div>

          <h1 className="text-[22px] font-bold text-foreground break-words">{poll.pollTitle}</h1>
          <p className="mt-1.5 text-sm text-[var(--color-text-secondary)] break-words">{poll.pollQuestion}</p>

          <dl className="mt-5 grid grid-cols-1 sm:grid-cols-2 gap-4 text-sm">
            <Meta label="Recipients" value={describeAudience(poll.audience)} />
            <Meta label="Results" value={describeVisibility(poll)} />
            <Meta label="Opened" value={formatDateTime(poll.startDateTime)} />
            <Meta label={isClosed ? "Closed" : "Closes"} value={formatDateTime(poll.endDateTime)} />
          </dl>
        </div>

        <div className="px-6 sm:px-8 py-6">
          {results ? (
            <PollResultsView
              poll={poll}
              viewer={user}
              results={results}
              participants={participants} />
          ) : (
            <LoadingState message="Loading results…" />
          )}
        </div>

        {actionError && (
          <div className="px-6 sm:px-8 py-3 bg-[var(--color-error)]/10 border-t border-[var(--color-error)]/25 text-[13px] font-semibold text-[var(--color-error)]">
            ⚠ {actionError}
          </div>
        )}

        {canManage && (
          <div className="flex flex-wrap justify-end gap-3 px-6 sm:px-8 py-5 bg-[#f8fafc] border-t border-[var(--color-text-border)]/70">
            {!isClosed && (
              <>
                <button
                  type="button"
                  onClick={() => setExtendOpen(true)}
                  disabled={busy || !online}
                  className="inline-flex items-center gap-2 min-h-[48px] h-11 px-5 rounded-xl border border-[var(--color-text-border)] bg-white text-sm font-semibold text-foreground hover:bg-[var(--color-workspace)] disabled:opacity-50">
                  <CalendarClock className="h-4 w-4" /> <Trans i18nKey="dateTime.extendEndDate" />
                </button>
                <button
                  type="button"
                  onClick={() => setCloseOpen(true)}
                  disabled={busy || !online}
                  className="inline-flex items-center gap-2 min-h-[48px] h-11 px-5 rounded-xl border border-[var(--color-error)]/40 bg-white text-sm font-semibold text-[var(--color-error)] hover:bg-[var(--color-error)]/5 disabled:opacity-50">
                  <XCircle className="h-4 w-4" /> <Trans i18nKey="formActions.closePoll" />
                </button>
              </>
            )}
            <button
              type="button"
              onClick={() => downloadCsv("poll-results.csv", buildResultsCsv(poll, results, participants))}
              disabled={!isClosed || !results}
              title={isClosed ? undefined : "Export unlocks once the poll is closed."}
              className="inline-flex items-center gap-2 min-h-[48px] h-11 px-5 rounded-xl bg-[var(--color-primary)] text-white text-sm font-semibold hover:bg-[var(--color-accent)] disabled:opacity-40 disabled:cursor-not-allowed">
              <Download className="h-4 w-4" /> <Trans i18nKey="formActions.exportToCsv" />
            </button>
          </div>
        )}
      </div>

      <ExtendEndDateModal
        open={extendOpen}
        currentEnd={poll.endDateTime}
        loading={busy}
        onConfirm={(date) => runAction(() => extendEndDate(poll.pollId, date), "End date extended.")}
        onClose={() => setExtendOpen(false)} />

      <ClosePollDialog
        open={closeOpen}
        loading={busy}
        onConfirm={() => runAction(() => closePoll(poll.pollId), "Poll closed. No further votes can be submitted.")}
        onClose={() => setCloseOpen(false)} />

      {toast && <Toast tone="success" onDone={() => setToast("")}>✓ {toast}</Toast>}
    </AppLayout>
  );
}

/** ESS variant — same screen, employee entry point. */
export function ESSPollResultsPage() {
  return <PollResultsPage audience="ess" />;
}
